import { useCallback, useEffect, useRef } from 'react'
import { TradeAccountType } from '../../../utils/enums'
import { useAccounts, useAppState } from '../../../utils/hooks'
import { useCurrencyConverter } from '../../../utils/useCurrencyConverter'
import {
    AccountCreatedInput,
    DepositEventAction,
    DepositEventInput,
    DashboardBaseInput,
    PlatformEventAction,
    PlatformEventInput,
    ProfileEventInput,
    ReferralEventAction,
    ReportDownloadedInput,
    SupportChannel,
    TransferEventAction,
    TransferEventInput,
    WithdrawalEventAction,
    WithdrawalEventInput,
    DASHBOARD_LANDED_SESSION_KEY,
    buildAccountCreatedEvent,
    buildBankAccountAddedEvent,
    buildKycApprovedEvent,
    buildDashboardLandedEvent,
    buildDepositEvent,
    buildPlatformEvent,
    buildProfileEvent,
    buildReferralEvent,
    buildReportDownloadedEvent,
    buildSupportEvent,
    buildTransferEvent,
    buildVerificationPromptViewedEvent,
    buildWithdrawalEvent,
} from '../../../utils/floodlightEvents'
import { setDedupUserScope } from '../Dashboard/Deposit/utils/depositEventDedup'
import { hasDepositEventFired, markDepositEventFiredMemory, persistDepositEventFired } from '../Dashboard/Deposit/utils/depositEventDedup'

export const useDashboardAnalytics = () => {
    const { userProfile, language, loggedIn } = useAppState()
    const { latestLiveAccount: primary, isDemo, liveAccounts } = useAccounts()

    useEffect(() => {
        setDedupUserScope(userProfile?.id)
    }, [userProfile?.id])

    const enabled = !isDemo

    const { getExchangeRate, ratesReady, isLoading: ratesLoading } = useCurrencyConverter()
    const getRateRef = useRef(getExchangeRate)
    useEffect(() => { getRateRef.current = getExchangeRate }, [getExchangeRate])

    const convertToUsd = useCallback((amount?: string | number | null, currency?: string | null): { value: number; currency: string } => {
        const num = typeof amount === 'number' ? amount : Number(amount)
        if (!Number.isFinite(num)) return { value: 0, currency: currency || '' }
        if (currency === 'USD') return { value: num, currency: 'USD' }
        if (!currency) return { value: num, currency: '' }
        const rate = getRateRef.current(currency, 'USD')
        if (rate == null || !Number.isFinite(rate)) return { value: num, currency }
        return { value: Math.round(num * rate * 100) / 100, currency: 'USD' }
    }, [])

    const withUsdAmount = useCallback(<T extends { amount?: string | number | null; currency?: string | null }>(params: T): T => {
        if (params.amount == null || params.amount === '') return params
        const { value, currency } = convertToUsd(params.amount, params.currency)
        return { ...params, amount: value, currency }
    }, [convertToUsd])

    const isLiveAccount = useCallback((target?: { id?: number; accountNumber?: number; type?: string } | null): boolean => {
        if (!target) return false
        if (target.type === TradeAccountType.Live) return true
        if (target.type === TradeAccountType.Demo) return false
        return liveAccounts.some(la =>
            (typeof target.id === 'number' && la.account.id === target.id) ||
            (typeof target.accountNumber === 'number' && la.account.accountNumber === target.accountNumber)
        )
    }, [liveAccounts])

    const country = userProfile?.country?.code2
    const countryName = userProfile?.country?.name
    const userId = userProfile?.id
    const email = userProfile?.email
    const accountId = primary?.account?.accountNumber
    const currency = primary?.account?.currency?.code
    const accountType = primary?.account?.accountTradingType?.accountTradingType
    const productTier = primary?.platformAccountType
    const platform = primary?.account?.platform?.name

    const buildBase = useCallback((): DashboardBaseInput => ({
        country: country || '',
        countryName: countryName || '',
        language: language || '',
        loggedIn,
        referrer: document.referrer,
        portalHost: window.location.hostname,
        pathname: window.location.pathname,
        userId,
        accountId,
        email,
        currency,
        accountType,
        productTier,
        platform,
    }), [country, countryName, language, loggedIn, userId, accountId, email, currency, accountType, productTier, platform])

    const safePush = useCallback((build: () => Record<string, string> | null): boolean => {
        if (!enabled) return false
        try {
            const payload = build()
            if (payload && window.dataLayer) {
                window.dataLayer.push(payload)
                return true
            }
        } catch { /* noop */ }
        return false
    }, [enabled])

    const fireAccountCreated = useCallback((params: AccountCreatedInput) => {
        safePush(() => buildAccountCreatedEvent(params, buildBase()))
    }, [safePush, buildBase])

    const fireKycApproved = useCallback((params: { org?: string | null; simplified?: boolean; eventId: string }): boolean => {
        return safePush(() => buildKycApprovedEvent({ ...buildBase(), org: params.org, simplified: params.simplified, eventId: params.eventId }))
    }, [safePush, buildBase])

    const fireReportDownloaded = useCallback((params: ReportDownloadedInput) => {
        safePush(() => buildReportDownloadedEvent({ ...params, format: params.format ?? 'csv' }, buildBase()))
    }, [safePush, buildBase])

    const fireVerificationPromptViewed = useCallback((action: string = 'viewed') => {
        safePush(() => buildVerificationPromptViewedEvent(buildBase(), action))
    }, [safePush, buildBase])

    const fireBankAccountAdded = useCallback((status?: string | null) => {
        safePush(() => buildBankAccountAddedEvent({ status }, buildBase()))
    }, [safePush, buildBase])

    const fireSupport = useCallback((channel: SupportChannel) => {
        safePush(() => buildSupportEvent(channel, buildBase()))
    }, [safePush, buildBase])

    const fireDeposit = useCallback((action: DepositEventAction, params?: DepositEventInput): boolean => {
        const txnId = params?.transactionId
        const dedupAction = (action === 'submitted' || action === 'success') && txnId != null && txnId !== '' ? action : null
        if (dedupAction && hasDepositEventFired(dedupAction, txnId)) return false
        const pushed = safePush(() => buildDepositEvent(action, withUsdAmount(params ?? {}), buildBase()))
        if (pushed && dedupAction) {
            markDepositEventFiredMemory(dedupAction, txnId)
            if (dedupAction === 'submitted') {
                setTimeout(() => persistDepositEventFired(dedupAction, txnId), 1500)
            } else {
                persistDepositEventFired(dedupAction, txnId)
            }
        }
        return pushed
    }, [safePush, buildBase, withUsdAmount])

    const fireWithdrawal = useCallback((action: WithdrawalEventAction, params?: WithdrawalEventInput) => {
        safePush(() => buildWithdrawalEvent(action, withUsdAmount(params ?? {}), buildBase()))
    }, [safePush, buildBase, withUsdAmount])

    const fireTransfer = useCallback((action: TransferEventAction, params?: TransferEventInput) => {
        safePush(() => buildTransferEvent(action, withUsdAmount(params ?? {}), buildBase()))
    }, [safePush, buildBase, withUsdAmount])

    const firePlatform = useCallback((action: PlatformEventAction, params: PlatformEventInput) => {
        safePush(() => buildPlatformEvent(action, params, buildBase()))
    }, [safePush, buildBase])

    const fireProfile = useCallback((params: ProfileEventInput) => {
        safePush(() => buildProfileEvent(params, buildBase()))
    }, [safePush, buildBase])

    const fireReferral = useCallback((action: ReferralEventAction) => {
        safePush(() => buildReferralEvent(action, buildBase()))
    }, [safePush, buildBase])

    const fireDashboardLanded = useCallback(() => {
        if (!enabled) return
        try { if (sessionStorage.getItem(DASHBOARD_LANDED_SESSION_KEY)) return } catch { /* noop */ }
        safePush(() => buildDashboardLandedEvent(buildBase()))
        try { sessionStorage.setItem(DASHBOARD_LANDED_SESSION_KEY, '1') } catch { /* noop */ }
    }, [enabled, safePush, buildBase])

    return {
        enabled,
        isLiveAccount,
        buildBase,
        convertToUsd,
        ratesReady,
        ratesLoading,
        fireAccountCreated,
        fireKycApproved,
        fireReportDownloaded,
        fireVerificationPromptViewed,
        fireBankAccountAdded,
        fireSupport,
        fireDeposit,
        fireWithdrawal,
        fireTransfer,
        firePlatform,
        fireProfile,
        fireReferral,
        fireDashboardLanded,
    }
}
